import React from 'react';
import {
  formatNumber,
  formatPercentage,
  formatSpend
} from 'shared/src/domain/line-item/line-item-utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { clsx } from 'clsx';
import { faCaretDown, faCaretUp, faCircleDot } from '@fortawesome/free-solid-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { isNil } from 'lodash';

type StandardMetric = {
  type: 'number' | 'currency' | 'percentage';
  name: string;
  value: number;
};

type MarginMetric = {
  type: 'margin';
  value: number;
  target: number;
};

type DiscountMetric = {
  type: 'discount';
  planned: StandardMetric;
  target: StandardMetric;
};

type SecondaryMetric = StandardMetric | MarginMetric;

type Props = {
  children: React.ReactNode;
  isCampaign?: boolean;
};

export function HeroMetricsContainer({ children, isCampaign }: Props) {
  return (
    <div
      className={clsx(
        'flex flex-col gap-1 bg-gray-100 p-1',
        isCampaign ? 'rounded-b-md' : 'rounded-md'
      )}>
      {children}
    </div>
  );
}

export type HeroMetricProps = {
  primary: StandardMetric;
  secondary: StandardMetric | MarginMetric;
  tertiary?: StandardMetric | DiscountMetric;
};

export function HeroMetric({ primary, secondary, tertiary }: HeroMetricProps) {
  return (
    <div className="flex-1 rounded-sm bg-white px-2 py-1.5">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium capitalize">{primary.name}</div>
        <div className="text-lg font-semibold">{formatMetric(primary)}</div>
      </div>
      <div className="mt-1.5">
        {secondary.type === 'margin' ? (
          <MarginMetricCmp metric={secondary} />
        ) : (
          <SecondaryMetric metric={secondary} />
        )}
        {tertiary &&
          (tertiary.type === 'discount' ? (
            <DiscountMetric metric={tertiary} />
          ) : (
            <TertiaryMetric metric={tertiary} />
          ))}
      </div>
    </div>
  );
}

export type HeroMetricMessageProps = {
  message: string;
  additionalMessage?: string;
};

export function HeroMetricMessage({ message, additionalMessage }: HeroMetricMessageProps) {
  return (
    <div className="flex w-full items-center rounded-sm bg-blue-200">
      <div className="px-2 text-blue-800">
        <FontAwesomeIcon icon={faInfoCircle} />
      </div>
      <div className="flex-1 rounded-sm bg-blue-100 p-2 text-sm text-gray-800">
        <b>{message}</b>
        {additionalMessage}
      </div>
    </div>
  );
}

function SecondaryMetric({ metric }: { metric: StandardMetric }) {
  return (
    <div className="rounded bg-gray-200 text-center text-[12px] font-bold capitalize leading-5">
      {formatMetric(metric)} {metric.name}
    </div>
  );
}

function DiscountMetric({ metric }: { metric: DiscountMetric }) {
  const discount =
    metric.planned.value && metric.target.value
      ? Math.abs((metric.target.value - metric.planned.value) / metric.target.value)
      : 0;
  const hasDiscount = metric.target.value > metric.planned.value;
  const hasPremium = metric.target.value < metric.planned.value;

  return (
    <div
      key={`${metric.planned.name}-${metric.target.value}`}
      className="rounded text-center text-[12px] capitalize leading-5 text-gray-500">
      {formatMetric(metric.planned)} {metric.planned.name}
      <span className="mx-1">|</span>
      {formatMetric(metric.target)} {metric.target.name}
      {(hasDiscount || hasPremium) && (
        <span className="ml-2 rounded border border-gray-300 px-1 py-0.5">
          <FontAwesomeIcon
            icon={hasDiscount ? faCaretDown : faCaretUp}
            className={hasDiscount ? 'mr-1 text-gray-500' : 'mr-1 text-gray-500'}
          />
          {formatPercentage(discount)} {hasDiscount ? 'discount' : hasPremium ? 'premium' : ''}
        </span>
      )}
    </div>
  );
}

function TertiaryMetric({ metric }: { metric: StandardMetric }) {
  return (
    <div
      key={metric.name}
      className="rounded text-center text-[12px] capitalize leading-5 text-gray-500">
      {formatMetric(metric)} {metric.name}
    </div>
  );
}

function MarginMetricCmp({ metric }: { metric: MarginMetric }) {
  const { value, target } = metric;
  const diff = marginDiff(value, target);
  const icon = marginIcon(diff);

  return (
    <div
      className={clsx(
        'rounded-sm text-center text-[12px] font-bold capitalize leading-5',
        marginColor(diff),
        marginTextColor(diff)
      )}>
      {icon && <FontAwesomeIcon icon={icon} className={clsx('mr-1', marginIconColor(diff))} />}
      {diff !== 0 && `${formatMetricPercentage(diff)} `}
      {marginPerformanceText(diff)}
    </div>
  );
}

function formatMetric(metric: SecondaryMetric) {
  switch (metric.type) {
    case 'percentage':
    case 'margin':
      return formatMetricPercentage(metric.value);
    case 'number':
      return metric.value ? formatNumber(metric.value).toUpperCase() : '--';
    case 'currency':
      return metric.value ? formatSpend(metric.value).toUpperCase() : '--';
  }
}

export function formatMetricPercentage(value: number | undefined) {
  if (!value || value === Infinity) return '--';
  return formatPercentage(value);
}

function marginDiff(actual: number, target: number) {
  if (actual === 0 || target === 0) return undefined;
  return (actual - target) / target;
}

function marginIcon(performance?: number) {
  if (isNil(performance)) return null;
  return performance > 0 ? faCaretUp : performance < 0 ? faCaretDown : faCircleDot;
}

function marginColor(performance?: number) {
  if (isNil(performance)) return 'bg-gray-200';
  if (performance >= 0.2) return 'margin-warning-bg';
  if (performance > 0) return 'bg-[#C2F3D6]';
  if (performance < 0) return 'bg-[#FEEBEB]';
  return 'bg-gray-200';
}

function marginIconColor(performance?: number) {
  if (isNil(performance)) return null;
  if (performance >= 0.2) return 'text-[#181D1F]';
  if (performance > 0) return 'text-[#2CD673]';
  if (performance < 0) return 'text-[#F56060]';
  return 'text-gray-500';
}

function marginTextColor(performance?: number) {
  if (isNil(performance)) return 'text-[#181D1F]';
  if (performance >= 0.2) return 'text-[#181D1F]';
  if (performance > 0) return 'text-[#165F34]';
  if (performance < 0) return 'text-[#AD0B0B]';
  return 'text-[#181D1F]';
}

function marginPerformanceText(performance?: number) {
  if (isNil(performance)) return 'Above or below';
  return `${performance > 0 ? 'Above' : performance < 0 ? 'Below' : 'On'} planned`;
}
